'use client';

import { useCallback, useEffect, useEffectEvent, useRef } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import { analytics, resolvePayModule } from '@ci/analytics';
import {
  AppEvent,
  AppEventType,
  ConsumerProvidedEventTypes,
  useAppEvents,
} from '@ci/shared/events';
import { isBrowser } from '@ci/shared/utils';

import { installIntercomIdentity } from '~/lib/intercom/intercom-identity';
import { accountSlugFromPathname } from '~/lib/utils/route-pattern';

// PAY-3983: every identify carries the Intercom Messenger JWT. Installed at
// module load, not in an effect, because the first identify (sign-in, via the
// mapping below) can fire before this provider's effects have run.
if (isBrowser()) {
  installIntercomIdentity(analytics);
}

type AnalyticsMapping<
  T extends ConsumerProvidedEventTypes = NonNullable<unknown>,
> = {
  [K in AppEventType<T>]?: (event: AppEvent<T, K>) => unknown;
};

/**
 * Hook to subscribe to app events and map them to analytics actions
 * @param mapping
 */
function useAnalyticsMapping<T extends ConsumerProvidedEventTypes>(
  mapping: AnalyticsMapping<T>,
) {
  const appEvents = useAppEvents<T>();

  const subscribeToAppEvent = useEffectEvent(
    (
      eventType: AppEventType<T>,
      handler: (event: AppEvent<T, AppEventType<T>>) => unknown,
    ) => {
      appEvents.on(eventType, handler);
    },
  );

  const unsubscribeFromAppEvent = useEffectEvent(
    (
      eventType: AppEventType<T>,
      handler: (event: AppEvent<T, AppEventType<T>>) => unknown,
    ) => {
      appEvents.off(eventType, handler);
    },
  );

  useEffect(() => {
    const subscriptions = Object.entries(mapping).map(
      ([eventType, handler]) => {
        subscribeToAppEvent(eventType as AppEventType<T>, handler);

        return () =>
          unsubscribeFromAppEvent(eventType as AppEventType<T>, handler);
      },
    );

    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
    };
  }, [mapping]);
}

/**
 * Define a mapping of app events to analytics actions
 * Add new mappings here to track new events in the analytics service from app events
 */
const analyticsMapping: AnalyticsMapping = {
  'user.signedIn': (event) => {
    const { userId, ...traits } = event.payload;

    if (userId) {
      return analytics.identify(userId, traits);
    }
  },
  'user.signedUp': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'checkout.started': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'user.updated': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
};

function AnalyticsProviderBrowser(props: React.PropsWithChildren) {
  // Subscribe to app events and map them to analytics actions
  useAnalyticsMapping(analyticsMapping);

  // Report page views to the analytics service
  useReportPageView(useCallback((url) => analytics.trackPageView(url), []));

  // Module adoption, alongside the page view rather than instead of it
  // (US-24).
  useReportModuleEntry();

  return props.children;
}

/**
 * Provider for the analytics service
 */
export function AnalyticsProvider(props: React.PropsWithChildren) {
  if (!isBrowser()) {
    return props.children;
  }

  return <AnalyticsProviderBrowser>{props.children}</AnalyticsProviderBrowser>;
}

/**
 * Report entering a product module (US-24).
 *
 * ENTRY, not navigation: the module is compared against the last one
 * reported, so moving from the invoice list to an invoice and back is one
 * `feature.viewed`. Emitting per navigation would reproduce page views with
 * a label on them, and would make a module's apparent usage a function of
 * how many records it holds rather than how much it is used.
 *
 * A ref, not state: this drives no rendering, and setting state here would
 * re-render every page on navigation to record something nothing displays.
 */
function useReportModuleEntry() {
  const pathname = usePathname();
  // The ACCOUNT is part of the key, not just the module. This provider is
  // mounted at the root and survives a tenant switch, so moving from one
  // account's invoices to another's read as the same entry and the second
  // account never got a `feature.viewed` — on an event whose whole purpose
  // is per-account module adoption.
  const lastEntry = useRef<string | undefined>(undefined);

  const reportModule = useEffectEvent(() => {
    const currentModule = resolvePayModule(pathname ?? '');

    const accountSlug = accountSlugFromPathname(pathname ?? '');

    // `undefined` outside the authenticated product (marketing, auth, admin).
    // Recorded as "no entry" so the next real one is reported, rather than
    // being swallowed because the user happened to return to where they were.
    const entry = currentModule
      ? `${accountSlug ?? ''}|${currentModule}`
      : undefined;

    if (entry === lastEntry.current) return;

    lastEntry.current = entry;

    if (!currentModule) return;

    void analytics.trackEvent('feature.viewed', {
      module: currentModule,
      ...(accountSlug ? { accountSlug } : {}),
    });
  });

  useEffect(() => {
    reportModule();
    // Module entry is derived from the path alone.
  }, [pathname]);
}

/**
 * Hook to report page views to the analytics service
 * @param reportAnalyticsFn
 */
function useReportPageView(reportAnalyticsFn: (url: string) => unknown) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const callAnalyticsOnPathChange = useEffectEvent(() => {
    const url = [pathname, searchParams.toString()].filter(Boolean).join('?');

    return reportAnalyticsFn(url);
  });

  useEffect(() => {
    callAnalyticsOnPathChange();
    // call whenever the pathname changes
  }, [pathname]);
}
